import { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { clearToken, fetchSession } from "./lib/api";
import TelemetryBar from "./components/TelemetryBar";
import Gate from "./pages/Gate";
import AuthCallback from "./pages/AuthCallback";
import Interview from "./pages/Interview";
import SphereView from "./pages/SphereView";
import Debrief from "./pages/Debrief";
import Operator from "./pages/Operator";
import Privacy from "./pages/Privacy";
import Legal from "./pages/Legal";
import ResetPassword from "./pages/ResetPassword";
import InviteLanding from "./pages/InviteLanding";
import Help from "./pages/Help";
import TourHost from "./components/GuidedTour";

function Shell({ children, operatorOnly }) {
  const [session, setSession] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [denied, setDenied] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const s = await fetchSession();
      setSession(s);
      setElapsed(s.elapsed_seconds);
      if (operatorOnly && !s.user?.is_operator) setDenied(true);
    } catch {
      clearToken();
      nav("/", { replace: true });
    }
  }, [nav, operatorOnly]);

  useEffect(() => {
    load();
  }, [load]);

  // only tick while the clock is actually running, and resync periodically
  useEffect(() => {
    if (session?.timer?.state !== "running") return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [session?.timer?.state]);

  useEffect(() => {
    const t = setInterval(() => load(), 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!session) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="label">Resuming engagement…</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <TelemetryBar session={session} elapsed={elapsed} />
      <TourHost session={session} />
      {denied ? (
        <div className="flex flex-1 items-center justify-center" data-testid="operator-denied">
          <p className="label">Operator only. Your results live under Results.</p>
        </div>
      ) : (
        children({ session, setSession, elapsed })
      )}
    </div>
  );
}

const Protected = ({ render, operatorOnly }) => (
  <Shell operatorOnly={operatorOnly}>{render}</Shell>
);

function AppRouter() {
  const location = useLocation();
  // process a fresh OAuth fragment before anything else checks for a session
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Gate />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/reset/:token" element={<ResetPassword />} />
      <Route path="/i/:token" element={<InviteLanding />} />
      <Route path="/interview" element={<Protected render={(p) => <Interview {...p} />} />} />
      <Route path="/sphere" element={<Protected render={(p) => <SphereView {...p} />} />} />
      <Route path="/debrief" element={<Protected render={() => <Debrief />} />} />
      <Route path="/operator" element={<Protected operatorOnly render={() => <Operator />} />} />
      <Route path="/help" element={<Protected render={() => <Help />} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="h-full">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}
